import { useState, useCallback } from "react";
import { Keyboard, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLongPress } from "@/hooks/useLongPress";
import { AgentModeSwitcher } from "./AgentModeSwitcher";
import { VoiceAgentSheet } from "./VoiceAgentSheet";
import { WriteAgentSheet } from "./WriteAgentSheet";
import { useAgentMode } from "./useAgentMode";
import { useVoiceAgent } from "./useVoiceAgent";
import { useWriteAgent } from "./useWriteAgent";
import type { AgentMode } from "./types";

export function AgentFab() {
  const [agentMode, setAgentMode] = useAgentMode();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const voiceAgent = useVoiceAgent();
  const writeAgent = useWriteAgent();

  const handleModeChange = useCallback(
    (m: AgentMode) => {
      voiceAgent.reset();
      writeAgent.reset();
      setAgentMode(m);
    },
    [voiceAgent, writeAgent, setAgentMode],
  );

  const handlePress = useCallback(() => {
    if (agentMode === "voice") {
      voiceAgent.startRecording();
    } else {
      writeAgent.openSheet();
    }
  }, [agentMode, voiceAgent, writeAgent]);

  const handleLongPress = useCallback(() => setPopoverOpen(true), []);

  const longPressHandlers = useLongPress({
    onPress: handlePress,
    onLongPress: handleLongPress,
  });

  const isVoiceActive = voiceAgent.state === "recording";

  return (
    <>
      <AgentModeSwitcher
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        mode={agentMode}
        onModeChange={handleModeChange}
        align="end"
      >
        <button
          aria-label={`Agent (${agentMode} mode)`}
          className={cn(
            "fixed bottom-6 right-6 z-50",
            "h-14 w-14 rounded-full shadow-lg",
            "flex items-center justify-center",
            "bg-primary text-primary-foreground",
            "select-none touch-none",
            "transition-transform hover:scale-105 active:scale-95",
          )}
          style={
            {
              touchAction: "none",
              WebkitTouchCallout: "none",
              userSelect: "none",
            } as React.CSSProperties
          }
          {...longPressHandlers}
        >
          {agentMode === "voice" ? (
            <Mic className={cn("size-6", isVoiceActive && "animate-pulse")} />
          ) : (
            <Keyboard className="size-6" />
          )}
        </button>
      </AgentModeSwitcher>

      <VoiceAgentSheet
        isOpen={voiceAgent.isSheetOpen}
        state={voiceAgent.state}
        transcript={voiceAgent.transcript}
        response={voiceAgent.response}
        error={voiceAgent.error}
        elapsedSeconds={voiceAgent.elapsedSeconds}
        onStop={voiceAgent.stopRecording}
        onNewRequest={voiceAgent.startRecording}
        onClose={voiceAgent.reset}
      />
      <WriteAgentSheet
        isOpen={writeAgent.isSheetOpen}
        state={writeAgent.state}
        inputText={writeAgent.inputText}
        response={writeAgent.response}
        error={writeAgent.error}
        elapsedSeconds={writeAgent.elapsedSeconds}
        onInputChange={writeAgent.setInputText}
        onSubmit={writeAgent.submit}
        onNewRequest={() => {
          writeAgent.continueConversation();
        }}
        onClose={writeAgent.reset}
      />
    </>
  );
}
